const API_BASE_URL = process.env.REACT_APP_URL;

const joinUrl = (base, path) => {
  if (!base) return path;
  return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
};

const getPresignedPayload = async (file, contentType) => {
  const endpoint = joinUrl(API_BASE_URL, "presigned-url");
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: "Bearer " + localStorage.getItem("admintoken"),
    },
    body: JSON.stringify({
      originalFileName: file.name || "file",
      contentType,
    }),
  });

  if (!response.ok) {
    const bodyText = await response.text();
    throw new Error(
      `presigned URL 발급 실패 (${response.status}): ${
        bodyText || response.statusText
      }`
    );
  }

  return response.json();
};

const pickPresignedUrl = (payload) => payload?.presignedUrl;

// 백엔드가 presigned URL을 서명할 때 Content-Disposition을 서명 헤더에 포함하므로,
// PUT 요청에도 응답으로 받은 값을 그대로 실어야 서명이 일치한다(누락 시 S3 403).
const pickContentDisposition = (payload) => payload?.contentDisposition ?? null;

// presigned 응답 필드명이 fileUrl → fileKey로 변경됨(값은 S3 object key).
// 구버전(fileUrl)도 함께 보아 호환을 유지한다. normalizeFileUrl이 key/URL 모두 처리.
const pickFileUrl = (payload) => payload?.fileKey ?? payload?.fileUrl;
const pickFileId = (payload) =>
  payload?.fileId ??
  payload?.id ??
  payload?.data?.fileId ??
  payload?.data?.id ??
  payload?.result?.fileId ??
  payload?.result?.id ??
  null;

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || ""));

const normalizeFileUrl = (candidateUrl, presignedUrl) => {
  // 백엔드가 절대 URL을 주면 그대로 사용하고,
  // 그렇지 않으면 presigned URL(실제 버킷을 가리킴)에서 쿼리를 떼어 파생한다.
  if (isAbsoluteUrl(candidateUrl)) return candidateUrl;
  return deriveFileUrl(presignedUrl);
};

const deriveFileUrl = (presignedUrl) => {
  if (!presignedUrl) return null;
  const [urlWithoutQuery] = presignedUrl.split("?");
  return urlWithoutQuery || null;
};

const deriveFileKey = (fileUrl) => {
  if (!fileUrl) return null;
  try {
    return decodeURIComponent(new URL(fileUrl).pathname.slice(1));
  } catch {
    return null;
  }
};

/**
 * 저장된 이미지/첨부 값(전체 URL 또는 이미 key)을 서버가 기대하는 S3 object key로 변환.
 * 서버가 생성/수정 요청의 ...Key/fileKeys 필드에 key를 기대하므로,
 * 편집 시 재업로드하지 않은 "기존 이미지(GET이 전체 URL로 내려준 값)"를 보낼 때 사용한다.
 */
const toFileKey = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (!/^https?:\/\//i.test(raw)) return raw.replace(/^\/+/, ""); // 이미 key 형태
  return deriveFileKey(raw) || raw;
};

const uploadToS3 = async (file, isImage = false) => {
  const returnData = {};

  try {
    if (!file) {
      throw new Error("파일이 선택되지 않았습니다.");
    }

    const rawName = typeof file.name === "string" ? file.name : "file";
    const isXLSX = rawName.toLowerCase().endsWith(".xlsx");
    const fallbackExt = file.type ? `.${file.type.split("/").pop()}` : "";
    const dotIndex = rawName.lastIndexOf(".");
    const extFromName = dotIndex >= 0 ? rawName.substring(dotIndex) : fallbackExt;
    const safeExt = (isXLSX ? ".xlsx" : extFromName || "").replace(/[^a-zA-Z0-9.]/g, "");

    const baseName =
      dotIndex >= 0 ? rawName.substring(0, dotIndex) : rawName;
    const safeBaseName =
      baseName.replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 40) || "file";

    const key = `upload/${Date.now()}_${safeBaseName}${safeExt}`;
    const contentType = isXLSX
      ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      : file.type || "application/octet-stream";

    const presignedPayload = await getPresignedPayload(file, contentType);
    const presignedUrl = pickPresignedUrl(presignedPayload);
    const contentDisposition = pickContentDisposition(presignedPayload);

    if (!presignedUrl) {
      throw new Error("presigned URL 응답 형식을 확인해주세요.");
    }

    const uploadResponse = await fetch(presignedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": contentType,
        // 백엔드가 서명한 Content-Disposition을 그대로 전달(누락 시 서명 불일치로 403).
        ...(contentDisposition
          ? { "Content-Disposition": contentDisposition }
          : {}),
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      const bodyText = await uploadResponse.text();
      throw new Error(
        `S3 업로드 실패 (${uploadResponse.status}): ${
          bodyText || uploadResponse.statusText
        }`
      );
    }

    const finalFileUrl = normalizeFileUrl(pickFileUrl(presignedPayload), presignedUrl);
    const finalFileKey = deriveFileKey(finalFileUrl) || key;
    const finalFileId = pickFileId(presignedPayload);
    returnData.downloadLink = finalFileUrl;
    returnData.key = finalFileKey;
    returnData.fileId = finalFileId;

    if (isImage) {
      returnData.imageUrl = finalFileUrl;
    }
  } catch (error) {
    // console.error("Error uploading files to S3:", error);
    const reason =
      error?.code && error?.message
        ? `${error.code}: ${error.message}`
        : error?.message || "S3 업로드 오류";
    throw new Error(reason);
  }

  return returnData;
};

const deleteFilesInS3 = async (fileKeys) => {
  // Presigned 구조 전환 후에는 프론트에서 S3 삭제 권한을 갖지 않습니다.
  // 백엔드에서 삭제 API가 준비되면 해당 API 호출로 대체해야 합니다.
  if (!fileKeys || fileKeys.length === 0) return;
  console.warn("deleteFilesInS3 is skipped in presigned mode:", fileKeys);
};

export { deleteFilesInS3, toFileKey, uploadToS3 };
